import React, { useState } from 'react';
import { Box, Typography, CardContent, Paper, Stack, TextField, Button, Alert, CircularProgress } from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import SectionPaper from '../../common/components/styled-ui-elements/SectionPaper';
import PageCard from '../../common/components/styled-ui-elements/PageCard';
import { CenterSelector } from '../../common/components';
import { useCenterSelection } from '../../common/hooks/useCenterSelection';
import { WriteOffCandidatesGrid } from '../components/grids';
import { AccountSummaryDialog } from '../components/dialogs';
import { AccountView } from '../../../services/AccountService/types/accountTypes';

// Types
interface WriteoffCreateParams {
  company: string;
  center: string;
}

// Constants
const THEME_COLORS = {
  primary: '#162346',
  primaryHover: '#1a2a52',
  background: '#f8fafc',
  card: '#ffffff',
  text: {
    primary: '#1e293b',
    secondary: 'text.secondary'
  }
} as const;

// Custom hook for managing writeoff creation parameters
const useWriteoffCreateParams = (): WriteoffCreateParams => {
  const [searchParams] = useSearchParams();
  
  return {
    company: searchParams.get('company') || '',
    center: searchParams.get('center') || ''
  };
};

// Header Component
const PageHeader: React.FC<{ company: string; center: string }> = ({ company, center }) => (
  <Paper elevation={1} sx={{ p: 1.5, mb: 2 }}>
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      <Box>
        <Typography variant="h6" component="h1" sx={{ mb: 0.25, fontWeight: 600 }}>
          Write Off Candidates
        </Typography>
        <Typography variant="body2" color={THEME_COLORS.text.secondary} sx={{ fontSize: '0.85rem' }}>
          Select a patient account to create a writeoff request
          {company && center && (
            <span> | Company: {company} | Center: {center}</span>
          )}
        </Typography>
      </Box>
    </Stack>
  </Paper>
);



// Results Section Component
const ResultsSection: React.FC<{
  createError: string | null;
  onSelectAccount: (accountId: string) => void;
}> = ({ createError, onSelectAccount }) => (
  <>
    {/* Error Messages */}
    {createError && (
      <Alert severity="error" sx={{ mb: 3 }}>
        {createError}
      </Alert>
    )}

    {/* Write-off Candidates Grid */}
    <Box sx={{ mt: 3 }}>
      <WriteOffCandidatesGrid onSelectAccount={onSelectAccount} />
    </Box>
  </>
);

// Main Component
export const WriteoffCreate: React.FC = () => {
  const navigate = useNavigate();
  const params = useWriteoffCreateParams();
  const { handleCenterChange } = useCenterSelection();
  const [createError, setCreateError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedAccountId, setSelectedAccountId] = useState<string | null>(null);

  const handleSelectAccount = (accountId: string) => {
    setSelectedAccountId(accountId);
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setSelectedAccountId(null);
  };

  return (
    <Box sx={{ 
      minHeight: '100vh',
      backgroundColor: THEME_COLORS.background,
      py: 1.5,
      px: 2
    }}>
      <Box display="flex" justifyContent="center">
        <PageCard>
          <PageHeader company={params.company} center={params.center} />
          
          <CardContent sx={{ p: 3, backgroundColor: THEME_COLORS.card }}>
            <Box sx={{ mb: 3 }}>
              <CenterSelector value={params.center} onChange={handleCenterChange} />
            </Box>
            <ResultsSection
              createError={createError}
              onSelectAccount={handleSelectAccount}
            />
          </CardContent>
        </PageCard>
      </Box>

      <AccountSummaryDialog
        open={dialogOpen}
        accountId={selectedAccountId}
        onClose={handleDialogClose}
      />
    </Box>
  );
};

export default WriteoffCreate;
